import L from 'leaflet';
import { AlertTriangle, FlaskConical, Loader2, MapPin } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type LiveLocation, useLiveLocations, usePostLocations } from '../api/useLocations.js';
import { useRoster } from '../api/useRoster.js';
import { formatRelative } from '../lib/format.js';

// Sydney CBD — default map centre when we have no location data to anchor on.
const FALLBACK_CENTER: [number, number] = [-33.8688, 151.2093];

// Worker status ring around each marker. Red = active alarm, amber = active check-in,
// orange = on-shift but location-silent (tracking gap), green = recently seen
// (last 15 min), grey = stale (off-shift / old).
type MarkerStatus = 'alarm' | 'check_in' | 'gap' | 'live' | 'stale';

const statusFill: Record<MarkerStatus, string> = {
  alarm: '#ef4444',
  check_in: '#f59e0b',
  gap: '#f97316',
  live: '#10b981',
  stale: '#9ca3af',
};

function deriveStatus(
  loc: LiveLocation,
  info: { hasAlarm: boolean; hasCheckIn: boolean } | undefined,
  isGapped: boolean,
): MarkerStatus {
  if (info?.hasAlarm) return 'alarm';
  if (info?.hasCheckIn) return 'check_in';
  // On-shift but location-silent (server-detected). Distinct from an ordinary
  // off-shift stale point, because this worker is unmonitored right now.
  if (isGapped) return 'gap';
  const ageMs = Date.now() - new Date(loc.recorded_at).getTime();
  return ageMs < 15 * 60_000 ? 'live' : 'stale';
}

// minutes → "2h 14m" / "45m"
function fmtDur(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export default function LiveMap() {
  const locations = useLiveLocations();
  const roster = useRoster();
  const post = usePostLocations();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.CircleMarker>>(new Map());
  const [devError, setDevError] = useState<string | null>(null);

  // Build a quick lookup from user_id → display info + status flags for marker render.
  const userMeta = useMemo(() => {
    const map = new Map<
      string,
      {
        displayName: string;
        badge: string;
        hasAlarm: boolean;
        hasCheckIn: boolean;
        phone: string | null;
        alarmId: string | null;
      }
    >();
    for (const m of roster.data?.members ?? []) {
      map.set(m.user_id, {
        displayName: m.display_name,
        badge: [m.state, m.job_title].filter(Boolean).join(' '),
        hasAlarm: Boolean(m.active_alarm_id),
        hasCheckIn: Boolean(m.active_check_in_id),
        phone: m.phone_e164,
        alarmId: m.active_alarm_id,
      });
    }
    return map;
  }, [roster.data]);

  // Initialise Leaflet once on mount; destroy on unmount. The map instance is held in a
  // ref so marker updates don't rebuild it.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: FALLBACK_CENTER,
      zoom: 11,
      zoomControl: true,
    });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, []);

  // Sync markers whenever the live locations change. Existing markers move; new users
  // get added; vanished users get removed.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const locs = locations.data?.locations ?? [];
    const gappedIds = new Set((locations.data?.gaps ?? []).map((g) => g.user_id));
    const seen = new Set<string>();

    for (const loc of locs) {
      seen.add(loc.user_id);
      const meta = userMeta.get(loc.user_id);
      const status = deriveStatus(loc, meta, gappedIds.has(loc.user_id));
      const existing = markersRef.current.get(loc.user_id);
      if (existing) {
        existing.setLatLng([loc.lat, loc.lng]);
        existing.setStyle({ fillColor: statusFill[status], color: statusFill[status] });
        existing.setPopupContent(popupHtml(loc, meta, status));
      } else {
        const marker = L.circleMarker([loc.lat, loc.lng], {
          radius: 9,
          weight: 3,
          opacity: 0.9,
          color: statusFill[status],
          fillColor: statusFill[status],
          fillOpacity: 0.55,
        });
        marker.bindPopup(popupHtml(loc, meta, status));
        marker.addTo(map);
        markersRef.current.set(loc.user_id, marker);
      }
    }

    // Remove markers for users no longer in the locations response.
    for (const [userId, marker] of markersRef.current.entries()) {
      if (!seen.has(userId)) {
        marker.remove();
        markersRef.current.delete(userId);
      }
    }

    // Auto-fit on first meaningful load.
    if (locs.length > 0 && markersRef.current.size > 0) {
      const group = L.featureGroup([...markersRef.current.values()]);
      const bounds = group.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.2), { animate: false, maxZoom: 15 });
      }
    }
  }, [locations.data, userMeta]);

  const count = locations.data?.locations.length ?? 0;
  const gaps = locations.data?.gaps ?? [];
  const lastUpdated = locations.dataUpdatedAt
    ? formatRelative(new Date(locations.dataUpdatedAt))
    : '—';

  async function sendTestLocation() {
    setDevError(null);
    // Pick a point near Sydney with some jitter.
    const lat = -33.8688 + (Math.random() - 0.5) * 0.05;
    const lng = 151.2093 + (Math.random() - 0.5) * 0.05;
    try {
      await post.mutateAsync({
        points: [
          {
            recorded_at: new Date().toISOString(),
            lat,
            lng,
            accuracy_m: 10 + Math.random() * 15,
            battery_pct: 40 + Math.floor(Math.random() * 55),
            source: 'foreground',
          },
        ],
      });
    } catch (err) {
      setDevError(err instanceof Error ? err.message : 'failed');
    }
  }

  return (
    <div className="mx-auto flex h-full max-w-6xl flex-col">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <span className="eyebrow">Operations</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Live map
          </h1>
          <p className="text-sm text-ink-faint">
            {count} worker{count === 1 ? '' : 's'} visible · updated {lastUpdated}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Legend />
          {import.meta.env.DEV && (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={sendTestLocation}
                disabled={post.isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-emerald-400 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
                title="POST a random Sydney-area point as the current user"
              >
                {post.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FlaskConical className="h-3.5 w-3.5" />
                )}
                Send test location as me
              </button>
              {devError && <span className="text-xs text-red-600">{devError}</span>}
            </div>
          )}
        </div>
      </div>

      {locations.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load locations: {(locations.error as Error).message}
        </div>
      )}

      {gaps.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {gaps.length} worker{gaps.length === 1 ? '' : 's'} on shift not reporting location
          </div>
          <ul className="mt-1.5 space-y-0.5 text-amber-800">
            {gaps.map((g) => (
              <li key={g.user_id} className="flex items-center justify-between gap-4">
                <span className="font-medium">{g.display_name}</span>
                <span className="tabular-nums text-amber-700">
                  {g.last_location_at
                    ? `silent ${fmtDur(g.minutes_since)}`
                    : `no location this shift · ${fmtDur(g.minutes_since)}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-amber-700">
            Ring the worker and ask them to open the app — that re-arms tracking.
          </p>
        </div>
      )}

      {/* The map container is ALWAYS rendered so Leaflet can initialise once on mount.
          Conditionally rendering it (e.g. based on `count === 0`) meant the init effect
          ran against a null ref on first render, then never re-ran when the div later
          appeared. The empty-state hint floats above the map instead. */}
      <div className="relative">
        <div ref={containerRef} className="h-[600px] w-full panel" />
        {count === 0 && !locations.isLoading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="pointer-events-auto max-w-sm rounded-lg border border-line bg-white/95 p-6 text-center shadow-panel backdrop-blur-sm">
              <MapPin className="mx-auto mb-2 h-8 w-8 text-ink-mute" strokeWidth={1.5} />
              <div className="mb-1 font-medium text-ink">No worker locations yet</div>
              <p className="text-xs text-ink-faint">
                Workers upload locations from the mobile app. Use the dev button above to post a
                test point as yourself — it'll appear live.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function popupHtml(
  loc: LiveLocation,
  meta:
    | { displayName: string; badge: string; phone: string | null; alarmId: string | null }
    | undefined,
  status: MarkerStatus,
): string {
  const colour = statusFill[status];
  const name = meta?.displayName ?? loc.user_id;
  const badge = meta?.badge ?? '';
  const accuracy = loc.accuracy_m != null ? `±${Math.round(loc.accuracy_m)}m` : 'Accuracy unknown';
  const seen = formatRelative(new Date(loc.recorded_at));

  const badgeLine = badge
    ? `<div style="font-size: 11px; color: #6b7280; margin-top: 2px;">${escapeHtml(badge)}</div>`
    : '';

  // Battery colour-coded — a phone dying mid-shift is a safety signal, not trivia.
  const batteryLine =
    loc.battery_pct != null
      ? `<div style="font-size: 12px; color: ${
          loc.battery_pct < 15 ? '#dc2626' : loc.battery_pct < 30 ? '#d97706' : '#6b7280'
        };">${loc.battery_pct}% battery</div>`
      : '';

  // Actions: call the worker, and if they're in alarm jump straight to it.
  // (A plain link full-page-navigates rather than SPA-routes from a Leaflet
  // popup, which is acceptable for a one-off jump to the alarm detail.)
  const callLine = meta?.phone
    ? `<a href="tel:${escapeHtml(meta.phone)}" style="display:inline-block; margin-top:6px; font-size:12px; font-weight:600; color:#047857; text-decoration:none;">📞 Call ${escapeHtml(meta.phone)}</a>`
    : '';
  const alarmLine = meta?.alarmId
    ? `<a href="/alarms/${escapeHtml(meta.alarmId)}" style="display:block; margin-top:4px; font-size:12px; font-weight:600; color:#dc2626; text-decoration:none;">⚠ View active alarm →</a>`
    : '';

  return `
    <div style="font-family: system-ui, sans-serif; min-width: 170px;">
      <div style="display: flex; align-items: center; gap: 6px; font-weight: 600;">
        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 999px; background: ${colour};"></span>
        ${escapeHtml(name)}
      </div>
      ${badgeLine}
      <div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${accuracy}</div>
      ${batteryLine}
      <div style="font-size: 11px; color: #9ca3af; margin-top: 4px;" title="${escapeHtml(new Date(loc.recorded_at).toLocaleString())}">seen ${escapeHtml(seen)}</div>
      ${callLine}
      ${alarmLine}
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

function Legend() {
  return (
    <div className="flex items-center gap-3 rounded-md border border-line bg-white px-3 py-1 text-xs shadow-panel">
      {(
        [
          ['alarm', 'Alarm'],
          ['check_in', 'Check-in'],
          ['gap', 'No signal'],
          ['live', 'Live'],
          ['stale', 'Stale'],
        ] as const
      ).map(([key, label]) => (
        <div key={key} className="flex items-center gap-1 text-ink-faint">
          <span className="h-2 w-2 rounded-full" style={{ background: statusFill[key] }} />
          {label}
        </div>
      ))}
    </div>
  );
}
